import {
  FetchRequest,
  JsonRpcApiProviderOptions,
  JsonRpcPayload,
  JsonRpcProvider,
  JsonRpcResult,
  Network
} from 'ethers'

// Errors thrown by `JsonRpcProvider._send` are always transport/HTTP-level failures
// (connection refused, DNS error, client-side timeout, or a non-2xx response such as
// 429/5xx caught by `assertOk`). JSON-RPC *execution* errors (e.g. reverts, "method
// not found") are NOT thrown here — they come back as `{ error }` objects inside the
// result array and are handled upstream. Therefore any throw from a single-endpoint
// attempt is a safe signal to fail over to the next RPC URL without masking real
// contract-level failures.

// Cap a single attempt so a hung/slow RPC fails over promptly instead of blocking on
// ethers' 300s default. Balance/state/gas calls all complete well within this.
const DEFAULT_PER_REQUEST_TIMEOUT = 30000
// After failing over to a backup, keep using it for this long before re-probing the
// primary, so we don't hammer a down primary on every request but still return to it
// automatically once it recovers.
const PRIMARY_RECOVERY_MS = 60000

/**
 * A `JsonRpcProvider` that transparently fails over across a list of RPC URLs.
 *
 * Behaviour:
 * - Requests start at the primary (index 0 = `selectedRpcUrl`). When the primary is
 *   healthy there is zero overhead — the first attempt succeeds and returns.
 * - On a transport-level failure the request is retried against the next URL in the
 *   list until one answers or all are exhausted (then the last error is thrown, so we
 *   never swallow a genuine outage).
 * - After a failover, subsequent requests start from the last known-good URL for
 *   `PRIMARY_RECOVERY_MS`, then the primary is re-probed so we return to it once it is
 *   healthy again.
 *
 * `_getConnection()` is intentionally left untouched (still returns the primary URL) so
 * external callers that read `provider._getConnection().url` — e.g.
 * `ProvidersController.setProvider` deciding whether the RPC changed — keep behaving
 * exactly as with a plain `JsonRpcProvider`.
 */
export class FailoverJsonRpcProvider extends JsonRpcProvider {
  readonly #urls: string[]

  readonly #perRequestTimeout: number

  readonly #chainIdLabel: string

  // Index of the URL that following requests should start from (0 = primary).
  #activeIndex = 0

  // Timestamp of the last failover, used to decide when to re-probe the primary.
  #lastSwitchAt = 0

  constructor(
    urls: string[],
    network?: Network,
    options?: JsonRpcApiProviderOptions,
    perRequestTimeout: number = DEFAULT_PER_REQUEST_TIMEOUT
  ) {
    // Build the base provider against the primary URL so `_getConnection().url` and
    // static-network pinning behave identically to a plain JsonRpcProvider.
    super(urls[0], network, options)
    this.#urls = urls
    this.#perRequestTimeout = perRequestTimeout
    this.#chainIdLabel = network ? String(network.chainId) : 'unknown'
  }

  // The order in which URLs are tried for the next request. Starts at `#activeIndex`
  // and wraps around, so a promoted backup is preferred until the primary recovers.
  #buildOrder(): number[] {
    if (this.#activeIndex !== 0 && Date.now() - this.#lastSwitchAt > PRIMARY_RECOVERY_MS) {
      this.#activeIndex = 0
    }
    const n = this.#urls.length
    return Array.from({ length: n }, (_, i) => (this.#activeIndex + i) % n)
  }

  // eslint-disable-next-line no-underscore-dangle
  async _send(payload: JsonRpcPayload | JsonRpcPayload[]): Promise<JsonRpcResult[]> {
    const order = this.#buildOrder()
    let lastError: any

    for (const idx of order) {
      const url = this.#urls[idx]
      if (!url) continue

      const request = new FetchRequest(url)
      request.body = JSON.stringify(payload)
      request.setHeader('content-type', 'application/json')
      request.timeout = this.#perRequestTimeout

      try {
        // eslint-disable-next-line no-await-in-loop
        const response = await request.send()
        response.assertOk()
        let resp = response.bodyJson
        if (!Array.isArray(resp)) resp = [resp]

        // Promote the URL that answered so subsequent requests start there for a while.
        if (idx !== this.#activeIndex) {
          this.#activeIndex = idx
          this.#lastSwitchAt = Date.now()
          console.warn(
            `[FailoverRPC] chain ${this.#chainIdLabel}: switched to ${url}` +
              (lastError ? ` after error: ${lastError?.message || lastError}` : '')
          )
        }

        return resp
      } catch (error: any) {
        // Transport-level failure — remember it and try the next URL. If this was the
        // last URL, the error is rethrown below so the caller sees the real outage.
        lastError = error
      }
    }

    throw lastError
  }
}
